// =============================================================
// SCREENS 5 — Companion desktop / Mentions légales / Confidentialité / 404
// =============================================================
import React, { useEffect, useState } from 'react';
import { Icon, SecHead } from './components';
import { ddVersion } from './ddragon';

const CONTACT = 'contact@buildtherift.eu';
const ZIP_URL = '/downloads/btr-companion.zip';
const MANIFEST_URL = '/downloads/companion-version.json';

// ============================================================
// COMPANION — application de bureau (lecture du client League)
// ============================================================
function CompanionScreen() {
  const [manifest, setManifest] = useState(null);
  useEffect(() => {
    let on = true;
    fetch(MANIFEST_URL).then((r) => (r.ok ? r.json() : null)).then((m) => on && setManifest(m)).catch(() => {});
    return () => { on = false; };
  }, []);
  const features = [
    { t: 'Scout Clash automatique', d: "Dès que ton équipe est inscrite, le companion lit le lobby et prépare le rapport des cinq adversaires : rangs, pools, bans conseillés." },
    { t: 'Partie en cours', d: 'Rangs, portraits et forme des dix joueurs pendant le chargement, en 5v5 comme en Arena.' },
    { t: 'Résumé de fin de partie', d: 'Un récapitulatif copiable pour Discord sitôt la partie terminée.' },
    { t: 'Arena God complet', d: 'La grille exacte des champions avec un Top 1, lue directement dans ton client (le site web ne voit que les parties récentes).' },
    { t: 'Ready check automatique', d: "Optionnel : accepte les files pour toi. C'est la seule action que le companion effectue dans le client." },
    { t: 'Mises à jour automatiques', d: "Jamais pendant une partie : la mise à jour attend que tu sois sorti du jeu." },
  ];
  return (
    <div className="container anim-fade-in">
      <header style={{marginBottom: 32}}>
        <div className="eyebrow">Companion desktop · Windows</div>
        <h1 className="h1" style={{marginTop:10}}>Le compagnon qui <span style={{color:"var(--accent)"}}>lit ton client</span>.</h1>
        <p className="lead" style={{marginTop:14, maxWidth: 720}}>
          Certaines données n'existent que dans le client League of Legends : le lobby Clash, la partie
          en cours, ta grille Arena complète. Le companion les lit en local, en lecture seule, et les
          affiche au bon moment.
        </p>
      </header>

      <div className="g-2" style={{alignItems:"start", marginBottom: 36}}>
        <section className="card" style={{padding: 24}}>
          <SecHead title="Télécharger" meta={manifest ? `Version ${manifest.version} · publiée le ${new Date(manifest.publishedAt).toLocaleDateString('fr-FR')}` : 'Dernière version'}/>
          <a className="btn primary lg" href={ZIP_URL} download style={{display:"inline-flex"}}>
            <Icon name="download" size={16}/> btr-companion.zip
          </a>
          {manifest?.notes && <p className="muted" style={{fontSize:13, marginTop:14, lineHeight:1.5}}>Nouveautés : {manifest.notes}</p>}
          <div className="divider"/>
          <div className="muted" style={{fontSize:12, fontWeight:600, letterSpacing:"0.06em", textTransform:"uppercase", marginBottom:10}}>Installation (version développeur)</div>
          <ol style={{margin:0, paddingLeft:20, fontSize:13, lineHeight:1.7, color:"var(--ink-2)"}}>
            <li>Installe <strong>Node.js 18 ou plus récent</strong> si ce n'est pas déjà fait.</li>
            <li>Décompresse l'archive dans un dossier de ton choix.</li>
            <li>Dans ce dossier, lance <code className="mono">npm install</code> puis <code className="mono">npm start</code>.</li>
            <li>Ouvre League of Legends : le companion détecte le client tout seul.</li>
          </ol>
          <p className="muted" style={{fontSize:12, marginTop:12, marginBottom:0}}>
            Un installeur en un clic est prévu. En attendant, cette version est réservée aux joueurs à l'aise avec Node.js.
          </p>
        </section>
        <section className="stack">
          {features.map((f) => (
            <div key={f.t} className="card" style={{padding:18}}>
              <div className="h4" style={{fontSize:16}}>{f.t}</div>
              <p className="muted" style={{fontSize:13, marginTop:6, marginBottom:0, lineHeight:1.5}}>{f.d}</p>
            </div>
          ))}
        </section>
      </div>

      <section className="card" style={{padding: 20, borderLeft: "3px solid var(--accent)"}}>
        <div className="row" style={{gap:12, alignItems:"flex-start"}}>
          <Icon name="alert" size={18} style={{color:"var(--accent)", flexShrink:0, marginTop:2}}/>
          <p className="muted" style={{fontSize:13, margin:0, lineHeight:1.55}}>
            Le companion respecte les règles Riot pour les applications tierces : il lit l'API locale du
            client (LCU) et n'automatise aucune action de jeu. Le seul geste effectué à ta place, si tu
            l'actives, est l'acceptation du ready check. Aucune donnée n'est envoyée ailleurs que sur
            BuildTheRift.
          </p>
        </div>
      </section>
    </div>
  );
}

// ============================================================
// MENTIONS LÉGALES
// ============================================================
function LegalScreen() {
  return (
    <div className="container anim-fade-in" style={{maxWidth: 900}}>
      <header style={{marginBottom: 28}}>
        <div className="eyebrow">Informations légales</div>
        <h1 className="h1" style={{marginTop:10, fontSize:"clamp(32px,5vw,48px)"}}>Mentions légales</h1>
      </header>
      <div className="stack">
        <section className="card">
          <SecHead title="Éditeur du site"/>
          <p className="body">
            BuildTheRift est un projet personnel, sans but lucratif, édité par <strong>[Nom et prénom du responsable]</strong>,
            domicilié en France. Le site est accessible à l'adresse <a href="https://lol.buildtherift.eu" className="t-accent">lol.buildtherift.eu</a>.
          </p>
          <p className="body">Contact : <a href={`mailto:${CONTACT}`} className="t-accent">{CONTACT}</a></p>
        </section>
        <section className="card">
          <SecHead title="Hébergement"/>
          <p className="body">
            Le site est hébergé sur un serveur dédié fourni par <strong>IONOS SE</strong>, Elgendorfer Str. 57,
            56410 Montabaur, Allemagne.
          </p>
        </section>
        <section className="card">
          <SecHead title="Propriété intellectuelle"/>
          <p className="body">
            League of Legends, les champions, les illustrations (splash arts), les objets, les runes et
            toutes les images du jeu sont la propriété de <strong>Riot Games, Inc.</strong> et sont utilisés
            conformément à la politique Riot Games pour les applications tierces (« Legal Jibber Jabber »).
          </p>
          <p className="body">
            BuildTheRift n'est pas approuvé par Riot Games et ne reflète pas les opinions de Riot Games ni
            de quiconque participe officiellement à la production ou à la gestion des propriétés de Riot
            Games. Riot Games et toutes les propriétés associées sont des marques commerciales ou des
            marques déposées de Riot Games, Inc.
          </p>
          <p className="body">
            Le code, la mise en page et les textes de BuildTheRift sont la propriété de leur auteur. Les
            statistiques affichées sont calculées à partir de données publiques de la Riot Games API et de
            Data Dragon (patch {ddVersion()}).
          </p>
        </section>
        <section className="card">
          <SecHead title="Responsabilité"/>
          <p className="body">
            Les winrates, tier lists, builds et suggestions sont des agrégats statistiques fournis à titre
            indicatif. Ils peuvent contenir des erreurs ou être en décalage avec le patch en cours. L'éditeur
            ne saurait être tenu responsable de l'usage qui en est fait.
          </p>
        </section>
      </div>
    </div>
  );
}

// ============================================================
// CONFIDENTIALITÉ
// ============================================================
function PrivacyScreen() {
  return (
    <div className="container anim-fade-in" style={{maxWidth: 900}}>
      <header style={{marginBottom: 28}}>
        <div className="eyebrow">Vie privée</div>
        <h1 className="h1" style={{marginTop:10, fontSize:"clamp(32px,5vw,48px)"}}>Politique de confidentialité</h1>
        <p className="lead" style={{marginTop:14}}>La version courte : pas de compte, pas de cookie publicitaire, pas de revente. Le détail ci-dessous.</p>
      </header>
      <div className="stack">
        <section className="card">
          <SecHead title="Ce que le site stocke sur ton appareil"/>
          <p className="body">
            BuildTheRift n'utilise pas de cookies. Il conserve dans le stockage local de ton navigateur
            uniquement des préférences d'affichage : thème clair ou sombre, région de recherche, dernier
            joueur suivi sur les écrans Arena et Clash, cache des données Data Dragon, et l'état d'un
            tournoi en cours. Ces données ne quittent pas ton navigateur et s'effacent en vidant les données
            du site.
          </p>
        </section>
        <section className="card">
          <SecHead title="Données de jeu"/>
          <p className="body">
            Les profils, historiques de parties et classements affichés proviennent de la Riot Games API.
            Il s'agit de données publiques liées à un identifiant Riot (Nom#TAG). BuildTheRift conserve un
            échantillon de parties classées pour calculer ses statistiques agrégées. Si tu souhaites que
            ton identifiant ne soit plus affiché ni conservé, écris à <a href={`mailto:${CONTACT}`} className="t-accent">{CONTACT}</a>.
          </p>
        </section>
        <section className="card">
          <SecHead title="Classements des jeux"/>
          <p className="body">
            Lorsque tu enregistres un score, seuls le pseudo que tu saisis et le score sont conservés. Tu
            peux demander leur suppression à la même adresse.
          </p>
        </section>
        <section className="card">
          <SecHead title="Journaux techniques"/>
          <p className="body">
            Comme tout site, le serveur enregistre des journaux techniques (adresse IP, page demandée,
            horodatage) nécessaires à la sécurité et au diagnostic. Ils sont conservés au maximum un an et
            ne sont pas croisés avec d'autres données.
          </p>
        </section>
      </div>
    </div>
  );
}

// ============================================================
// 404
// ============================================================
function NotFoundScreen({ setRoute }) {
  return (
    <div className="container anim-fade-in" style={{textAlign:"center", paddingTop: 64, paddingBottom: 96}}>
      <div className="eyebrow">Erreur 404</div>
      <h1 className="h1" style={{marginTop:10}}>Cette page n'existe pas.</h1>
      <p className="lead" style={{marginTop:14, maxWidth: 520, marginLeft:"auto", marginRight:"auto"}}>
        Le lien est peut-être périmé, ou il manque un morceau. Tout ce que propose BuildTheRift est
        accessible depuis le menu.
      </p>
      <div className="row" style={{justifyContent:"center", marginTop:28, gap:10}}>
        <button className="btn primary lg" onClick={() => setRoute("home")}><Icon name="arrowL" size={16}/> Retour à l'accueil</button>
        <button className="btn outline lg" onClick={() => setRoute("champions")}>Voir les champions</button>
      </div>
    </div>
  );
}

export { CompanionScreen, LegalScreen, PrivacyScreen, NotFoundScreen };
